import React from "react";
import classNames from "classnames";

import "./TextArea.scss";

/**
 * Props for the TextArea component.
 *
 * @description The TextArea component is a multi-line text input control
 * with configurable character limits and styling options.
 *
 * @property {string} [placeHolder] - Placeholder text displayed when the textarea is empty.
 * @property {string} [className] - Additional CSS class names to apply to the textarea element.
 * @property {string} [value=""] - The current value of the textarea.
 * @property {number} [maxChars=500] - Maximum number of characters allowed in the textarea.
 * @property {(e: any) => void} [onChange] - Change event handler. Called when the textarea value changes.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Basic textarea
 * <TextArea
 *   placeHolder="Enter your message..."
 *   value={message}
 *   onChange={(e) => setMessage(e.target.value)}
 * />
 *
 * @example
 * // Textarea with custom character limit
 * <TextArea
 *   placeHolder="Enter description (max 1000 chars)"
 *   value={description}
 *   maxChars={1000}
 *   onChange={handleDescriptionChange}
 * />
 *
 * @example
 * // Textarea with custom styling
 * <TextArea
 *   placeHolder="Additional notes"
 *   value={notes}
 *   className="notes-textarea"
 *   maxChars={2000}
 *   onChange={handleNotesChange}
 *   testId="notes-textarea"
 * />
 */
interface Props {
  placeHolder?: string;
  className?: string;
  value?: string;
  maxChars?: number;
  onChange?: (e: any) => void;
  testId?: string;
}

/**
 * A multi-line text input component with character limit support.
 *
 * @description TextArea renders an HTML textarea element with configurable
 * placeholder text, character limits, and styling. It wraps the native textarea
 * with consistent styling and event handling.
 *
 * Features:
 * - Configurable maximum character limit
 * - Custom placeholder text support
 * - CSS class customization via className prop
 * - Test ID support for testing frameworks
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A textarea element with the configured properties
 *
 * @see Props for all available properties
 */
const TextArea: React.FC<Props> = (props) => {
  const {
    placeHolder,
    onChange,
    className,
    maxChars = 500,
    value = "",
    testId,
  } = props;

  const wrapClass = classNames("textarea-wrapper", className);

  const handleOnChange = (e: any) => {
    if (onChange) {
      onChange(e);
    }
  };
  return (
    <textarea
      className={wrapClass}
      placeholder={placeHolder}
      onChange={(e) => handleOnChange(e)}
      value={value}
      maxLength={maxChars}
      data-testid={testId}
    />
  );
};

export default TextArea;
